/**
 * The Mini Me moment: the shopper watches the AI find their face, cut the
 * head out, and drop it onto the product.
 *
 * The cut-out takes a few seconds, and on a Mini Me product it is the whole
 * point of the purchase — so rather than a spinner, the shopper's own photo is
 * shown being analysed (a scanning beam, a measuring mesh, steps that say what
 * is happening), then the face is bracketed, the background falls away around
 * a glowing head, and the head flies to exactly where it lands on the product.
 * The preview underneath has already been re-rendered with the head placed,
 * so when the overlay lifts, the real render — the print pixels — is there.
 *
 * Purely presentational: every decision (which head, where it goes) is made
 * by the caller and handed in; a failure here must never cost the photo.
 */

/** A rectangle in viewport pixels. */
export interface ScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Where the whole cut-out picture ends up on screen once placed. */
export interface HeadLanding {
  /** The unrotated picture's rectangle in viewport pixels. */
  image: ScreenRect;
  /** Its turn in degrees, about `pivot` (the slot's centre, viewport pixels). */
  rotation: number;
  pivot: { x: number; y: number };
  /** The slot, for the landing pulse. */
  slot: ScreenRect;
  /** The chosen head of a group photo, in fractions of the picture. */
  keep?: { x: number; y: number; w: number; h: number };
}

export interface HeadRevealLabels {
  /** What the AI is doing, shown in turn while the server works. */
  steps: string[];
  /** The face was found and the head cut out. */
  found: string;
  /** Several heads: the shopper is about to be asked which. */
  several: string;
  /** Shown briefly after the head lands. */
  landed: string;
}

export const HEAD_REVEAL_LABELS: HeadRevealLabels = {
  steps: [
    'AI analizira fotografiju…',
    'Pronalazim lice…',
    'Izdvajam kosu i konturu glave…',
    'Isecam glavu…',
  ],
  found: 'Glava je isečena!',
  several: 'Pronašli smo više lica — izaberi svoje',
  landed: 'Gotovo! Prevuci ili uveličaj lice po želji.',
};

/** A head's box in fractions of the picture. */
type Box = { x: number; y: number; w: number; h: number };

const reducedMotion = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** The reveal's decisions in the console, only with `localStorage.pzDebug = '1'`. */
function debug(...args: unknown[]): void {
  try {
    if (window.localStorage.getItem('pzDebug') === '1') console.info('[personalizy head]', ...args);
  } catch {
    // Storage blocked: stay silent.
  }
}

const wait = (ms: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, ms));

export class HeadReveal {
  private readonly root: HTMLElement;
  private readonly card: HTMLElement;
  private readonly photo: HTMLImageElement;
  private readonly cut: HTMLImageElement;
  private readonly brackets: HTMLElement;
  private readonly status: HTMLElement;
  private readonly statusText: HTMLElement;
  private stepTimer: number | undefined;
  private closed = false;
  private readonly reduced = reducedMotion();

  constructor(
    private readonly stage: HTMLElement,
    photoUrl: string | undefined,
    private readonly labels: HeadRevealLabels = HEAD_REVEAL_LABELS,
  ) {
    const root = document.createElement('div');
    root.className = 'pz-hc';
    root.setAttribute('role', 'status');
    root.setAttribute('aria-live', 'polite');
    root.innerHTML =
      '<div class="pz-hc-card">' +
      '<img class="pz-hc-photo" alt="" draggable="false">' +
      '<span class="pz-hc-mesh"></span>' +
      '<span class="pz-hc-beam"></span>' +
      '<img class="pz-hc-cut" alt="" draggable="false">' +
      '<span class="pz-hc-brackets"></span>' +
      '</div>' +
      '<div class="pz-hc-status"><i class="pz-hc-dot"></i><span class="pz-hc-text"></span>' +
      '<span class="pz-hc-bar"><i></i></span></div>';
    this.root = root;
    this.card = root.querySelector('.pz-hc-card') as HTMLElement;
    this.photo = root.querySelector('.pz-hc-photo') as HTMLImageElement;
    this.cut = root.querySelector('.pz-hc-cut') as HTMLImageElement;
    this.brackets = root.querySelector('.pz-hc-brackets') as HTMLElement;
    this.status = root.querySelector('.pz-hc-status') as HTMLElement;
    this.statusText = root.querySelector('.pz-hc-text') as HTMLElement;
    this.photo.crossOrigin = 'anonymous';
    this.cut.crossOrigin = 'anonymous';
    if (photoUrl) {
      this.photo.addEventListener('load', () => this.fitCard(), { once: true });
      this.photo.src = photoUrl;
    }
    this.fitCard();
  }

  /** Mounts the overlay and starts the "analysing" phase. */
  start(): void {
    if (this.root.parentElement !== this.stage) this.stage.appendChild(this.root);
    this.fitCard();
    // A popup opening with the upload has no size for a moment; the card is
    // fitted again as soon as it has. Timers, not frames: a tab in the
    // background pauses frames, and a card measured at zero never flies.
    let tries = 0;
    const settle = (): void => {
      if (this.closed) return;
      if (this.stage.getBoundingClientRect().width > 0) {
        this.fitCard();
        return;
      }
      if (++tries < 100) window.setTimeout(settle, 100);
    };
    window.setTimeout(settle, 0);
    let step = 0;
    this.say(this.labels.steps[0] ?? '');
    window.clearInterval(this.stepTimer);
    this.stepTimer = window.setInterval(() => {
      step = Math.min(step + 1, this.labels.steps.length - 1);
      this.say(this.labels.steps[step] ?? '');
      if (step === this.labels.steps.length - 1) window.clearInterval(this.stepTimer);
    }, 1500);
    // Next frame, so the entrance transition runs from the initial styles
    // (a timer too: a background tab pauses frames, and the overlay must not
    // stay invisible when the shopper comes back).
    const enter = (): void => this.root.classList.add('is-in');
    requestAnimationFrame(enter);
    window.setTimeout(enter, 120);
  }

  /**
   * Plays the cut: brackets close on the head(s), the background falls away,
   * and — when `land` says where — the head flies onto the product.
   *
   * `chosen` is the index of the head that will be placed; undefined when the
   * shopper is about to be asked which one (the overlay then just steps back).
   */
  async reveal(
    cutUrl: string,
    heads: Box[],
    chosen: number | undefined,
    land?: () => Promise<HeadLanding | undefined>,
  ): Promise<void> {
    if (this.closed) return;
    window.clearInterval(this.stepTimer);
    // The cut and the landing are prepared in parallel with the reveal: the
    // caller re-renders the product underneath while the shopper watches.
    const landing = land
      ? land().catch((error: unknown) => {
          debug('placing the head failed', error);
          return undefined;
        })
      : Promise.resolve(undefined);
    this.cut.src = cutUrl;
    // Bounded: a background tab may defer decoding, and the moment must move on.
    await Promise.race([this.cut.decode().catch(() => undefined), wait(2500)]);
    if (this.closed) return;
    // The popup may only now have its size.
    this.fitCard();
    this.root.classList.add('is-found');
    this.say(chosen === undefined && heads.length > 1 ? this.labels.several : this.labels.found);

    // Brackets start at the card's edges and close in on each head.
    this.brackets.textContent = '';
    const marks = heads.map((head, index) => {
      const mark = document.createElement('span');
      mark.className = 'pz-hc-mark' + (index === chosen ? ' is-chosen' : '');
      mark.innerHTML = '<i></i><i></i><i></i><i></i>';
      Object.assign(mark.style, { left: '0%', top: '0%', width: '100%', height: '100%' });
      this.brackets.appendChild(mark);
      return { mark, head };
    });
    await nextFrame();
    for (const { mark, head } of marks) {
      const pad = 0.04;
      Object.assign(mark.style, {
        left: `${(head.x - head.w * pad) * 100}%`,
        top: `${(head.y - head.h * pad) * 100}%`,
        width: `${head.w * (1 + 2 * pad) * 100}%`,
        height: `${head.h * (1 + 2 * pad) * 100}%`,
      });
    }
    await wait(this.reduced ? 0 : 520);
    if (this.closed) return;
    // The cut: background dims away, the head glows.
    this.root.classList.add('is-cut');
    await wait(this.reduced ? 150 : 760);
    if (this.closed) return;

    const target = chosen === undefined ? undefined : await landing;
    if (this.closed) return;
    debug('reveal: landing', target, 'reduced motion', this.reduced);
    if (!target || this.reduced) {
      this.close(target ? this.labels.landed : undefined);
      if (target) this.pulse(target.slot);
      return;
    }
    await this.fly(target);
  }

  /** The cut-out failed: say so, stop the motion, and step aside. */
  fail(message: string): void {
    if (this.closed) return;
    window.clearInterval(this.stepTimer);
    this.say(message);
    this.root.classList.add('is-error');
    window.setTimeout(() => this.close(), 2600);
  }

  /** Fades the overlay out and removes it. */
  close(toast?: string): void {
    if (this.closed) return;
    this.closed = true;
    window.clearInterval(this.stepTimer);
    this.root.classList.add('is-out');
    window.setTimeout(() => this.root.remove(), 380);
    if (toast) this.toast(toast);
  }

  /** Removes everything at once — the product was swapped mid-animation. */
  dispose(): void {
    this.closed = true;
    window.clearInterval(this.stepTimer);
    this.root.remove();
  }

  /** The head leaves the photo and lands where the render has it. */
  private async fly(target: HeadLanding): Promise<void> {
    // The card may have been measured before the popup had its size.
    if (!this.card.getBoundingClientRect().width) this.fitCard();
    const from = this.cut.getBoundingClientRect();
    const host = this.root.getBoundingClientRect();
    if (!from.width || !host.width || !target.image.width) {
      this.close(this.labels.landed);
      return;
    }
    // Outer: sits at the landing rectangle, turned about the slot's centre.
    // Inner: the picture, flown from where it is in the photo card.
    const outer = document.createElement('span');
    outer.className = 'pz-hc-flyer';
    const left = target.image.left - host.left;
    const top = target.image.top - host.top;
    Object.assign(outer.style, {
      left: `${left}px`,
      top: `${top}px`,
      width: `${target.image.width}px`,
      height: `${target.image.height}px`,
      transformOrigin: `${target.pivot.x - target.image.left}px ${target.pivot.y - target.image.top}px`,
    });
    const inner = document.createElement('img');
    inner.alt = '';
    inner.draggable = false;
    inner.src = this.cut.src;
    const keep = target.keep;
    if (keep) {
      inner.style.clipPath = `inset(${keep.y * 100}% ${(1 - keep.x - keep.w) * 100}% ${(1 - keep.y - keep.h) * 100}% ${keep.x * 100}%)`;
    }
    outer.appendChild(inner);
    this.root.appendChild(outer);
    this.root.classList.add('is-flying');

    const dx = from.left - target.image.left;
    const dy = from.top - target.image.top;
    const sx = from.width / target.image.width;
    const sy = from.height / target.image.height;
    const duration = 900;
    const easing = 'cubic-bezier(.22,.9,.24,1)';
    const flights: Animation[] = [];
    if (typeof inner.animate === 'function') {
      flights.push(inner.animate(
        [
          { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, filter: 'drop-shadow(0 0 0 rgba(0,0,0,0))' },
          { transform: `translate(${dx * 0.45}px, ${dy * 0.45 - 18}px) scale(${(sx + 1) / 2 * 1.06}, ${(sy + 1) / 2 * 1.06})`, filter: 'drop-shadow(0 14px 18px rgba(0,0,0,.35))', offset: 0.55 },
          { transform: 'none', filter: 'drop-shadow(0 0 0 rgba(0,0,0,0))' },
        ],
        { duration, easing, fill: 'both' },
      ));
      if (target.rotation) {
        flights.push(outer.animate(
          [{ transform: 'rotate(0deg)' }, { transform: `rotate(${target.rotation}deg)` }],
          { duration, easing, fill: 'both' },
        ));
      }
      // A background tab pauses animations; the overlay must never be left
      // covering the product because nobody was watching.
      await Promise.race([
        Promise.all(flights.map((f) => f.finished.catch(() => undefined))),
        wait(duration + 700),
      ]);
    }
    if (this.closed) return;
    // The flown head is unmasked and above the body; the render underneath
    // has the real one. Hand over with a short fade.
    this.pulse(target.slot);
    outer.classList.add('is-landed');
    await wait(260);
    this.close(this.labels.landed);
  }

  /** A ring that breathes once around the slot the head landed in. */
  private pulse(slot: ScreenRect): void {
    const host = this.stage.getBoundingClientRect();
    if (!slot.width || !host.width) return;
    const ring = document.createElement('span');
    ring.className = 'pz-hc-pulse';
    const size = Math.min(slot.width, slot.height);
    Object.assign(ring.style, {
      left: `${slot.left - host.left + slot.width / 2 - size / 2}px`,
      top: `${slot.top - host.top + slot.height / 2 - size / 2}px`,
      width: `${size}px`,
      height: `${size}px`,
    });
    this.stage.appendChild(ring);
    window.setTimeout(() => ring.remove(), 1400);
  }

  private toast(text: string): void {
    const toast = document.createElement('span');
    toast.className = 'pz-hc-toast';
    toast.textContent = text;
    this.stage.appendChild(toast);
    window.setTimeout(() => toast.classList.add('is-out'), 2600);
    window.setTimeout(() => toast.remove(), 3000);
  }

  private say(text: string): void {
    this.statusText.textContent = text;
  }

  /** Sizes the photo card to the photo's own shape, inside the stage. */
  private fitCard(): void {
    const aspect = (this.photo.naturalWidth || 1) / (this.photo.naturalHeight || 1);
    const stage = this.stage.getBoundingClientRect();
    // No size yet (a popup still opening): keep whatever the card has; the
    // settle loop and each phase fit it again once the stage is laid out.
    if (!stage.width || !stage.height) return;
    const stageW = stage.width;
    const stageH = stage.height;
    // Inside the stage, with room for the status pill underneath.
    const width = Math.min(stageW * 0.74, stageH * 0.6 * aspect);
    this.card.style.width = `${Math.round(width)}px`;
    this.card.style.height = `${Math.round(width / aspect)}px`;
  }

  /** For tests: the overlay element. */
  get element(): HTMLElement {
    return this.root;
  }

  /** For tests: whether the overlay has finished. */
  get finished(): boolean {
    return this.closed;
  }

  /** The status line's element, for tests. */
  get statusElement(): HTMLElement {
    return this.status;
  }
}

/** Two frames, so a style change is laid out before the next — or a short timer, in a paused tab. */
function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      resolve();
    };
    requestAnimationFrame(() => requestAnimationFrame(finish));
    window.setTimeout(finish, 80);
  });
}
